import type { DeepSummary, Paper, PaperStatus, Summary } from "@/api/types";
import type { ListenMode } from "@/lib/audioPolicy";

import { AudioPlayer, type ListenCommand } from "./audio-player";

const STATUS_LABEL: Record<PaperStatus, string> = {
  new: "未处理",
  review: "复筛",
  deep_read: "精读",
  keep: "保留",
  later: "稍后",
  archive: "归档",
};

function list(value: string[] | string | undefined): string {
  return Array.isArray(value) ? value.join("、") : value || "—";
}

function safeUrl(paper: Paper): string {
  const candidate = paper.arxiv_id ? `https://arxiv.org/abs/${encodeURIComponent(paper.arxiv_id)}` : paper.pdf_url;
  if (!candidate) return "https://arxiv.org";
  try {
    const url = new URL(candidate, window.location.origin);
    return ["http:", "https:"].includes(url.protocol) && !url.username && !url.password
      ? url.href
      : "https://arxiv.org";
  } catch {
    return "https://arxiv.org";
  }
}

function SummaryView({ summary, deep = false }: { summary: Summary | DeepSummary | null | undefined; deep?: boolean }) {
  if (!summary) return <p class="muted-text">{deep ? "全文报告仍在准备" : "暂无 AI 总结"}</p>;
  const extended = summary as DeepSummary;
  return (
    <details class="paper-summary">
      <summary>{summary.one_line || (deep ? "展开全文复筛报告" : "展开 AI 总结")}</summary>
      <dl>
        <div><dt>研究问题</dt><dd>{summary.problem || "—"}</dd></div>
        <div><dt>方法</dt><dd>{summary.method || "—"}</dd></div>
        <div><dt>创新点</dt><dd>{summary.novelty || "—"}</dd></div>
        {deep && <div><dt>实验与结果</dt><dd>{[extended.experiments, extended.results].filter(Boolean).join("；") || "—"}</dd></div>}
        {deep && <div><dt>局限</dt><dd>{extended.limitations || "—"}</dd></div>}
        <div><dt>相关性</dt><dd>{summary.relevance || "—"}</dd></div>
        {!deep && <div><dt>数据集</dt><dd>{list(summary.datasets)}</dd></div>}
      </dl>
    </details>
  );
}

export function PaperCard({
  paper,
  busy = false,
  onStatus,
  onRestore,
  onDetail,
  onRead,
  onOpen,
  onSkip,
  onRetryAnnotations,
  listenMode,
  listenCommand,
  variant = "list",
}: {
  paper: Paper;
  busy?: boolean;
  onStatus: (paper: Paper, status: PaperStatus) => void;
  onRestore: (paper: Paper) => void;
  onDetail: (paper: Paper) => void;
  onRead: (paper: Paper) => void;
  onOpen: (paper: Paper) => void;
  onSkip?: (paper: Paper) => void;
  onRetryAnnotations?: (paper: Paper) => void;
  listenMode?: ListenMode;
  listenCommand?: ListenCommand;
  variant?: "list" | "swipe";
}) {
  const statusActions: PaperStatus[] = paper.status === "new"
    ? ["archive", "later", "keep", "review"]
    : paper.status === "review"
      ? ["archive", "later", "keep", "deep_read"]
      : [];
  const artifacts = paper.artifacts ?? paper.artifact_status;
  const annotationsFailed = ["failed", "blocked", "error"].includes(
    (artifacts?.annotations_status ?? artifacts?.annotation_status ?? "").toLowerCase(),
  );

  const mainContent = (
    <>
      <div class="paper-title-row">
        <h2><a href={safeUrl(paper)} target="_blank" rel="noopener noreferrer">{paper.title}</a></h2>
        <button type="button" class="btn btn-ghost" onClick={() => onDetail(paper)}>详情</button>
      </div>
      <p class="authors">{paper.authors.slice(0, 8).join(", ")}{paper.authors.length > 8 ? " 等" : ""}</p>
      <div class="meta">
        <span class="badge score">相关性 {paper.relevance_score}</span>
        <span class={`badge status-${paper.status}`}>{STATUS_LABEL[paper.status]}</span>
        {paper.matched_keywords.slice(0, 5).map((keyword) => <span class="badge" key={keyword}>{keyword}</span>)}
      </div>
      <SummaryView summary={paper.status === "review" || paper.status === "deep_read" ? paper.deep_summary : paper.summary} deep={paper.status === "review" || paper.status === "deep_read"} />
      {artifacts && (
        <p class="artifact-line">
          Zotero：条目 {artifacts.item_status ?? "—"} · PDF {artifacts.pdf_status ?? "—"} · 批注 {artifacts.annotations_status ?? artifacts.annotation_status ?? "—"}
        </p>
      )}
      {variant === "swipe" && (
        <section class="swipe-section">
          <h3>原文摘要</h3>
          <p class="swipe-abstract">{paper.abstract || "暂无摘要"}</p>
        </section>
      )}
    </>
  );

  return (
    <article class={`card paper-card${variant === "swipe" ? " swipe-paper-card" : ""}${busy ? " action-pending" : ""}`} aria-busy={busy} tabIndex={variant === "swipe" ? -1 : undefined}>
      {variant === "swipe" ? <div class="swipe-body">{mainContent}</div> : mainContent}
      <div class={`actions${variant === "swipe" ? " swipe-actions" : ""}`}>
        <AudioPlayer paper={paper} mode={listenMode} command={listenCommand} showStartButton={false} />
        {variant === "swipe" ? (
          <>
            <button type="button" class="btn btn-archive" disabled={busy} onClick={() => onStatus(paper, "archive")}><span class="label">归档</span><span class="dir" aria-hidden="true">←</span></button>
            {onSkip && <button type="button" class="btn btn-skip" disabled={busy} onClick={() => onSkip(paper)}><span class="label">跳过</span><span class="dir" aria-hidden="true">↓</span></button>}
            <button type="button" class="btn btn-keep" disabled={busy} onClick={() => onStatus(paper, "keep")}><span class="label">保留</span><span class="dir" aria-hidden="true">↑</span></button>
            <button type="button" class={`btn btn-${paper.status === "new" ? "review" : "deep"}`} disabled={busy} onClick={() => onStatus(paper, paper.status === "new" ? "review" : "deep_read")}><span class="label">{paper.status === "new" ? "复筛" : "精读"}</span><span class="dir" aria-hidden="true">→</span></button>
          </>
        ) : (
          <>
            {paper.status === "new" && onSkip && (
              <button type="button" class="btn btn-skip" disabled={busy} onClick={() => onSkip(paper)}>跳过本轮</button>
            )}
            {statusActions.map((status) => (
              <button key={status} type="button" class={`btn btn-${status === "deep_read" ? "deep" : status}`} disabled={busy} onClick={() => onStatus(paper, status)}>
                {STATUS_LABEL[status]}
              </button>
            ))}
          </>
        )}
        {["keep", "later", "archive"].includes(paper.status) && (
          <button type="button" class="btn btn-ghost" disabled={busy} onClick={() => onRestore(paper)}>恢复筛选</button>
        )}
        {paper.status === "deep_read" && (
          <>
            <button type="button" class="btn btn-deep" disabled={busy} onClick={() => onOpen(paper)}>去 Zotero 阅读</button>
            <button type="button" class="btn btn-ghost" disabled={busy} onClick={() => onRead(paper)}>{paper.read_at ? "撤销读完" : "标记读完"}</button>
          </>
        )}
        {annotationsFailed && onRetryAnnotations && (
          <button type="button" class="btn btn-ghost" disabled={busy} onClick={() => onRetryAnnotations(paper)}>重试候选批注</button>
        )}
      </div>
    </article>
  );
}

export function PaperDetail({ paper }: { paper: Paper }) {
  return (
    <div class="paper-detail">
      <p class="authors">{paper.authors.join(", ")}</p>
      <div class="meta"><span class="badge score">相关性 {paper.relevance_score}</span></div>
      <SummaryView summary={paper.summary} />
      <SummaryView summary={paper.deep_summary} deep />
      <section>
        <h3>原文摘要</h3>
        <p>{paper.abstract}</p>
      </section>
    </div>
  );
}
